/**
 * « Nouveau lead » (conception 5.2 et 8.4), en deux temps : retrouver
 * l'etablissement dans le registre des entreprises, puis completer ce que le
 * registre ignore. Les champs officiels ne se saisissent jamais : l'API les
 * relit chez l'INSEE a partir du seul SIRET.
 */
import { useEffect, useRef, useState } from 'react';
import type { FormEvent, RefObject } from 'react';
import { flushSync } from 'react-dom';
import { Link, useNavigate } from 'react-router-dom';
import type { UseMutationResult } from '@tanstack/react-query';
import { ArrowLeft, Landmark, Plus, Search } from 'lucide-react';
import { z } from 'zod';
import { siretValide } from '@leads/shared';
import type { CreerLead, EtablissementTrouveDTO, LeadDetailDTO } from '@leads/shared';
import { ErreurApi, aCodeErreur, erreurDeChamp, messageErreur } from '../lib/api';
import { useAuth } from '../lib/auth';
import { avecDe, formaterDate, formaterSiret, pluriel } from '../lib/format';
import { useValeurDifferee } from '../lib/hooks';
import { usePermissions } from '../lib/permissions';
import { prochaineEtapeParDefaut, versProchaineEtape } from '../lib/prochaine-etape';
import type { BrouillonProchaineEtape } from '../lib/prochaine-etape';
import { useCreerLead, useRechercheEntreprises } from '../lib/requetes';
import { ChampCommercial } from './ChampCommercial';
import { ChampsProchaineEtape } from './ChampsProchaineEtape';
import { Badge, Bandeau, Bouton, CaseACocher, Champ, Modale, Rouet, classesBouton, signalerErreursDeChamps, useToast } from './ui';

/** Le debit de l'INSEE est compte (30 requetes par minute) : on attend la fin de la frappe. */
export const DELAI_RECHERCHE_ENTREPRISE_MS = 400;
export const LONGUEUR_MIN_RECHERCHE = 3;

interface SaisieRecherche { texte: string; codePostal: string }

/** Entreprise retenue ; `etablissement` absent quand le SIRET a ete saisi a la main. */
interface EntrepriseChoisie { siret: string; etablissement: EtablissementTrouveDTO | null }

/** Requete de recherche, ou `null` sous 3 caracteres ; un SIREN ou un SIRET perd ses espaces. */
export function normaliserRecherche(texte: string, codePostal: string): { q: string; codePostal: string } | null {
  const propre = texte.trim();
  const q = /^[\d\s]+$/.test(propre) ? propre.replace(/\s+/g, '') : propre;
  if (q.length < LONGUEUR_MIN_RECHERCHE) return null;
  const cp = codePostal.trim();
  return { q, codePostal: /^\d{5}$/.test(cp) ? cp : '' };
}

/**
 * Les messages de l'API nomment le registre par son nom technique : on les
 * traduit par code. `gereIntegrations` (`jetons.gerer`) : un registre non
 * configure se dit a l'administrateur par la variable a regler sur le serveur
 * (l'ecran des integrations ne la touche pas) ; les autres sont invites a le prevenir.
 */
export function messageErreurRegistre(erreur: unknown, gereIntegrations = false): string {
  if (aCodeErreur(erreur, 'RECHERCHE_NOM_INDISPONIBLE')) {
    return "La recherche par nom est momentanément indisponible. Saisissez plutôt le SIREN (9 chiffres) ou le SIRET (14 chiffres) de l'entreprise.";
  }
  if (aCodeErreur(erreur, 'SERVICE_INDISPONIBLE')) {
    // Cle absente ou refusee par l'INSEE (meme code) : « corrigez » couvre le second cas.
    return `La connexion au registre des entreprises n'est pas configurée : la création de leads est indisponible. ${
      gereIntegrations
        ? "Renseignez ou corrigez la clé INSEE (SIRENE_API_KEY) dans la configuration du serveur, puis redémarrez l'API."
        : 'Prévenez un administrateur.'
    }`;
  }
  if (aCodeErreur(erreur, 'SIRET_INTROUVABLE')) return 'Ce SIRET est inconnu du registre des entreprises. Vérifiez le numéro.';
  if (erreur instanceof ErreurApi && erreur.statut === 502) return 'Le registre des entreprises ne répond pas. Réessayez dans quelques instants.';
  return messageErreur(erreur);
}

/** `details` d'un 409 LEAD_EXISTANT (plan 04) : l'identifiant du lead et s'il peut s'ouvrir. */
function detailsLeadExistant(erreur: unknown): { leadId: string; visible: boolean } | null {
  if (!(erreur instanceof ErreurApi) || typeof erreur.details !== 'object' || erreur.details === null) return null;
  const { leadId, visible } = erreur.details as Record<string, unknown>;
  return typeof leadId === 'string' && typeof visible === 'boolean' ? { leadId, visible } : null;
}

function adresseLisible(etablissement: EtablissementTrouveDTO): string {
  const commune = [etablissement.codePostal, etablissement.ville].filter(Boolean).join(' ');
  return [etablissement.adresse, commune].filter(Boolean).join(', ') || 'Adresse inconnue';
}

export interface ChampsNouveauLead {
  telephone: string;
  email: string;
  siteWeb: string;
  contactPrenom: string;
  contactNom: string;
  contactFonction: string;
  /** `null` : vivier. Lu seulement avec `leads.attribuer`. */
  proprietaireId: string | null;
  planifier: boolean;
  prochaineEtape: BrouillonProchaineEtape;
  notes: string;
}

export interface ErreursNouveauLead {
  telephone?: string;
  email?: string;
  siteWeb?: string;
  contactNom?: string;
  proprietaireId?: string;
  prochaineEtape?: string;
}

/** Meme regle que `creerLeadSchema.email` : l'erreur s'affiche avant l'envoi. */
const EMAIL = z.string().email();

const MESSAGE_DATE_REFUSEE = "Cette date n'est pas valide : choisissez la date de la première étape.";

export function verifierNouveauLead(champs: ChampsNouveauLead): ErreursNouveauLead {
  const erreurs: ErreursNouveauLead = {};
  const email = champs.email.trim();
  if (email && !EMAIL.safeParse(email).success) erreurs.email = 'Adresse e-mail invalide.';
  if (!champs.contactNom.trim() && (champs.contactPrenom.trim() || champs.contactFonction.trim())) erreurs.contactNom = 'Indiquez le nom du contact.';
  if (champs.planifier && !versProchaineEtape(champs.prochaineEtape)) erreurs.prochaineEtape = 'Choisissez la date de la première étape.';
  return erreurs;
}

/** Corps de `POST /leads` : seul le SIRET est requis, les champs vides sont omis. */
export function corpsNouveauLead(siret: string, champs: ChampsNouveauLead, options: { attribuer: boolean; moiId: string | undefined }): CreerLead {
  const texte = (valeur: string): string | null => valeur.trim() || null;
  const corps: CreerLead = { siret };
  const telephone = texte(champs.telephone);
  if (telephone) corps.telephone = telephone;
  const email = texte(champs.email);
  if (email) corps.email = email;
  const siteWeb = texte(champs.siteWeb);
  if (siteWeb) corps.siteWeb = siteWeb;
  const nom = texte(champs.contactNom);
  if (nom) corps.contact = { nom, prenom: texte(champs.contactPrenom), fonction: texte(champs.contactFonction) };
  // Omis, le proprietaire est le createur : seul `leads.attribuer` designe un autre compte, ou le vivier.
  if (options.attribuer && champs.proprietaireId !== options.moiId) corps.proprietaireId = champs.proprietaireId;
  const etape = champs.planifier ? versProchaineEtape(champs.prochaineEtape) : null;
  if (etape) corps.prochaineEtape = etape;
  const notes = texte(champs.notes);
  if (notes) corps.notes = notes;
  return corps;
}

export function BoutonNouveauLead() {
  const [ouverte, setOuverte] = useState(false);
  // Une instance neuve a chaque ouverture : la fenetre repart toujours de la recherche.
  const [instance, setInstance] = useState(0);
  return (
    <>
      <Bouton variante="primaire" icone={<Plus aria-hidden="true" />} aria-label="Nouveau lead"
        onClick={() => { setInstance((precedente) => precedente + 1); setOuverte(true); }}>
        <span className="hidden sm:inline">Nouveau lead</span>
      </Bouton>
      <ModaleNouveauLead key={instance} ouverte={ouverte} onFermer={() => setOuverte(false)} />
    </>
  );
}

export function ModaleNouveauLead({ ouverte, onFermer }: { ouverte: boolean; onFermer: () => void }) {
  const [saisie, setSaisie] = useState<SaisieRecherche>({ texte: '', codePostal: '' });
  const [choix, setChoix] = useState<EntrepriseChoisie | null>(null);
  // Portee par la fenetre, pas par le formulaire : une creation en cours n'est
  // jamais detachee de sa suite (toast, fiche ouverte), que le formulaire demonte emporterait.
  const creer = useCreerLead();
  // Cible du focus initial (recherche) : passee a Modale (refFocusInitial), qui
  // capte alors correctement le declencheur avant de deplacer le focus. Un
  // second champ, celui du formulaire, recoit le focus au choix.
  const refRecherche = useRef<HTMLInputElement>(null);
  const refPremierChamp = useRef<HTMLInputElement>(null);
  // Le premier rendu est deja gere par Modale (refFocusInitial) : seul un
  // changement d'etape ulterieur (choix, retour) doit redeplacer le focus,
  // jamais de setTimeout qui le forcerait a l'ouverture.
  const refMonte = useRef(false);
  useEffect(() => {
    if (!refMonte.current) { refMonte.current = true; return; }
    (choix ? refPremierChamp : refRecherche).current?.focus();
  }, [choix]);

  return (
    <Modale titre="Nouveau lead" ouverte={ouverte} onFermer={onFermer} largeur="max-w-2xl" refFocusInitial={refRecherche} verrouillee={creer.isPending}
      description={choix ? 'Étape 2 sur 2 : complétez ce que le registre ne connaît pas.' : "Étape 1 sur 2 : retrouvez l'entreprise dans le registre des entreprises (Insee)."}>
      {choix
        ? (
          <FormulaireNouveauLead entreprise={choix} creer={creer} refPremierChamp={refPremierChamp} onTermine={onFermer}
            // Le formulaire suivant repart sans l'echec de celui-ci.
            onRetour={() => { creer.reset(); setChoix(null); }} />
        )
        : <RechercheEntreprise saisie={saisie} onSaisie={setSaisie} refRecherche={refRecherche} onChoisir={setChoix} onQuitter={onFermer} />}
    </Modale>
  );
}

function RechercheEntreprise({ saisie, onSaisie, refRecherche, onChoisir, onQuitter }: {
  saisie: SaisieRecherche;
  onSaisie: (saisie: SaisieRecherche) => void;
  /** Focalise a l'ouverture (Modale) et au retour depuis le formulaire (ModaleNouveauLead). */
  refRecherche: RefObject<HTMLInputElement | null>;
  onChoisir: (entreprise: EntrepriseChoisie) => void;
  /** Ferme la fenetre avant d'ouvrir une fiche existante. */
  onQuitter: () => void;
}) {
  const texte = useValeurDifferee(saisie.texte, DELAI_RECHERCHE_ENTREPRISE_MS);
  const codePostal = useValeurDifferee(saisie.codePostal, DELAI_RECHERCHE_ENTREPRISE_MS);
  const requete = normaliserRecherche(texte, codePostal);
  const resultats = useRechercheEntreprises(requete);
  const { peut } = usePermissions();
  const gereIntegrations = peut('jetons.gerer');
  const erreur = requete !== null && resultats.isError ? resultats.error : null;
  const reessayer = aCodeErreur(erreur, 'SOURCE_EXTERNE');
  const trouves = requete !== null && !resultats.isError ? resultats.data : undefined;

  return (
    <div className="space-y-4">
      <div className="grid gap-3 sm:grid-cols-[minmax(0,1fr)_9rem]">
        <Champ libelle="Nom de l'entreprise ou SIRET">
          {(id) => (
            <div className="relative">
              <Search aria-hidden="true" className="pointer-events-none absolute top-1/2 left-3 -translate-y-1/2 text-encre-tenue" />
              <input ref={refRecherche} id={id} type="search" autoComplete="off" className="champ pl-9" placeholder="Ex. Atelier du Centre, 812 345 676 00017"
                value={saisie.texte} onChange={(evenement) => onSaisie({ ...saisie, texte: evenement.target.value })} />
            </div>
          )}
        </Champ>
        <Champ libelle="Code postal">
          {(id) => (
            <input id={id} inputMode="numeric" maxLength={5} autoComplete="off" className="champ" placeholder="Facultatif"
              value={saisie.codePostal} onChange={(evenement) => onSaisie({ ...saisie, codePostal: evenement.target.value })} />
          )}
        </Champ>
      </div>

      {/* Region vivante : un etat court, jamais la liste des resultats (relue en entier a chaque mise a jour),
          ni le bandeau d'erreur, qui s'annonce deja lui-meme (`role="alert"`). */}
      <div role="status">
        {requete === null ? (
          <p className="aide">Saisissez au moins 3 caractères : nom, enseigne, SIREN ou SIRET.</p>
        ) : resultats.isError ? null : !resultats.data ? (
          <p className="flex items-center gap-2 text-sm text-encre-discrete"><Rouet />Recherche dans le registre des entreprises…</p>
        ) : resultats.data.length === 0 ? (
          <p className="text-sm text-encre-discrete">
            Aucun établissement ne correspond à « {requete.q} ». Vérifiez l'orthographe, ajoutez le code postal ou saisissez le SIRET.
          </p>
        ) : (
          <p className="text-sm text-encre-discrete">{pluriel(resultats.data.length, 'établissement trouvé', 'établissements trouvés')}</p>
        )}
      </div>
      {erreur && (
        <Bandeau ton={aCodeErreur(erreur, 'RECHERCHE_NOM_INDISPONIBLE') ? 'avertissement' : 'danger'} role="alert" titre="Recherche impossible"
          actions={reessayer ? <Bouton taille="sm" onClick={() => void resultats.refetch()}>Réessayer</Bouton> : undefined}>
          {messageErreurRegistre(erreur, gereIntegrations)}
        </Bandeau>
      )}
      {trouves && trouves.length > 0 && (
        <ul aria-label="Établissements trouvés" className="divide-y divide-bordure rounded-lg border border-bordure">
          {trouves.map((etablissement) => (
            <LigneEtablissement key={etablissement.siret} etablissement={etablissement} onChoisir={onChoisir} onQuitter={onQuitter} />
          ))}
        </ul>
      )}

      <SaisieSiret onChoisir={onChoisir} />
    </div>
  );
}

function LigneEtablissement({ etablissement, onChoisir, onQuitter }: {
  etablissement: EtablissementTrouveDTO;
  onChoisir: (entreprise: EntrepriseChoisie) => void;
  onQuitter: () => void;
}) {
  const deja = etablissement.dejaEnBase;
  return (
    <li className="flex flex-wrap items-center justify-between gap-3 px-4 py-3">
      <div className="min-w-0">
        <p className="font-medium text-encre">
          {etablissement.denomination}
          {etablissement.enseigne && <span className="font-normal text-encre-discrete"> · {etablissement.enseigne}</span>}
        </p>
        <p className="text-sm text-encre-discrete">{adresseLisible(etablissement)}</p>
        <p className="text-xs text-encre-discrete tabular-nums">SIRET {formaterSiret(etablissement.siret)}</p>
        {(!etablissement.actif || deja) && (
          <div className="mt-1.5 flex flex-wrap gap-1.5">
            {!etablissement.actif && <Badge ton="avertissement">Établissement fermé</Badge>}
            {deja?.visible && <Badge ton="info">Déjà dans vos leads</Badge>}
            {deja && !deja.visible && <Badge>Déjà suivi par un collègue</Badge>}
          </div>
        )}
      </div>
      {deja ? (
        deja.visible && (
          <Link to={`/leads/${deja.leadId}`} onClick={onQuitter} aria-label={`Ouvrir la fiche ${avecDe(etablissement.denomination)}`}
            className={classesBouton('secondaire', 'sm')}>
            Ouvrir la fiche
          </Link>
        )
      ) : (
        <Bouton taille="sm" variante="primaire" aria-label={`Choisir ${etablissement.denomination}`}
          onClick={() => onChoisir({ siret: etablissement.siret, etablissement })}>
          Choisir
        </Bouton>
      )}
    </li>
  );
}

function SaisieSiret({ onChoisir }: { onChoisir: (entreprise: EntrepriseChoisie) => void }) {
  const [ouverte, setOuverte] = useState(false);
  const [siret, setSiret] = useState('');
  const [erreur, setErreur] = useState<string | null>(null);
  const refSiret = useRef<HTMLInputElement>(null);

  if (!ouverte) {
    // Le bouton disparait avec le clic : le champ qui le remplace prend le focus, qui tomberait sinon sur la page.
    const ouvrir = (): void => {
      flushSync(() => setOuverte(true));
      refSiret.current?.focus();
    };
    return (
      <p className="border-t border-bordure pt-4 text-sm text-encre-discrete">
        Vous connaissez le SIRET ?{' '}
        <button type="button" className="lien font-medium" onClick={ouvrir}>Saisir le SIRET à la main</button>
      </p>
    );
  }

  const continuer = (evenement: FormEvent<HTMLFormElement>): void => {
    evenement.preventDefault();
    const chiffres = siret.replace(/\s+/g, '');
    if (siretValide(chiffres)) { onChoisir({ siret: chiffres, etablissement: null }); return; }
    const message = "Ce SIRET n'est pas valide : vérifiez les 14 chiffres.";
    signalerErreursDeChamps(() => setErreur(message), [[message, refSiret]]);
  };

  return (
    <form onSubmit={continuer} noValidate className="flex flex-wrap items-start gap-2 border-t border-bordure pt-4">
      <Champ libelle="SIRET (14 chiffres)" erreur={erreur} className="min-w-56 flex-1">
        {(id, decritPar) => (
          <input ref={refSiret} id={id} inputMode="numeric" autoComplete="off" className="champ" placeholder="812 345 676 00017" aria-describedby={decritPar}
            aria-invalid={erreur ? true : undefined} value={siret} onChange={(evenement) => { setSiret(evenement.target.value); setErreur(null); }} />
        )}
      </Champ>
      <Bouton type="submit" variante="primaire" className="sm:mt-6">Continuer</Bouton>
    </form>
  );
}

function IdentiteOfficielle({ entreprise }: { entreprise: EntrepriseChoisie }) {
  const etablissement = entreprise.etablissement;
  return (
    <div className="rounded-lg border border-bordure bg-surface-douce p-4">
      <p className="font-semibold text-encre">{etablissement ? etablissement.denomination : 'Entreprise à identifier'}</p>
      <p className="text-sm text-encre-discrete">
        {etablissement ? adresseLisible(etablissement) : "Le nom et l'adresse seront repris du registre à l'enregistrement."}
      </p>
      <p className="mt-1 text-xs text-encre-discrete tabular-nums">
        SIRET {formaterSiret(entreprise.siret)}
        {etablissement?.dateCreation ? ` · créé le ${formaterDate(etablissement.dateCreation)}` : ''}
      </p>
      {etablissement && !etablissement.actif && <Badge ton="avertissement" className="mt-2">Établissement fermé</Badge>}
      <p className="mt-2 flex items-center gap-1.5 text-xs text-encre-discrete">
        <Landmark aria-hidden="true" size={14} />Informations du registre des entreprises (Insee), non modifiables.
      </p>
    </div>
  );
}

function ErreurCreation({ erreur, onOuvrir }: { erreur: unknown; onOuvrir: () => void }) {
  const { peut } = usePermissions();
  const gereIntegrations = peut('jetons.gerer');
  if (!aCodeErreur(erreur, 'LEAD_EXISTANT')) {
    return (
      <Bandeau ton="danger" role="alert" titre="Le lead n'a pas été créé">
        {messageErreurRegistre(erreur, gereIntegrations)}
      </Bandeau>
    );
  }
  const details = detailsLeadExistant(erreur);
  return (
    <Bandeau ton="avertissement" role="alert" titre="Cette entreprise est déjà dans la base"
      actions={details?.visible ? (
        <Link to={`/leads/${details.leadId}`} onClick={onOuvrir} className={classesBouton('secondaire', 'sm')}>Ouvrir la fiche existante</Link>
      ) : undefined}>
      {details?.visible ? "Ouvrez sa fiche plutôt que d'en créer une seconde." : 'Elle est suivie par un collègue : demandez à un responsable de vous la confier.'}
    </Bandeau>
  );
}

function FormulaireNouveauLead({ entreprise, creer, refPremierChamp, onRetour, onTermine }: {
  entreprise: EntrepriseChoisie;
  /** Creation, portee par la fenetre (`ModaleNouveauLead`). */
  creer: UseMutationResult<LeadDetailDTO, Error, CreerLead>;
  /** Premier champ du formulaire (Téléphone) : recoit le focus au choix (ModaleNouveauLead). */
  refPremierChamp: RefObject<HTMLInputElement | null>;
  onRetour: () => void;
  /** Ferme la fenetre (lead cree, ou fiche existante ouverte). */
  onTermine: () => void;
}) {
  const { utilisateur } = useAuth();
  const { peut } = usePermissions();
  const attribuer = peut('leads.attribuer');
  const toast = useToast();
  const naviguer = useNavigate();
  const [champs, setChamps] = useState<ChampsNouveauLead>(() => ({
    telephone: '', email: '', siteWeb: '', contactPrenom: '', contactNom: '', contactFonction: '',
    proprietaireId: utilisateur?.id ?? null,
    planifier: true,
    // Un lead cree a la main se travaille vite : premier appel le jour ouvre suivant.
    prochaineEtape: prochaineEtapeParDefaut(new Date(), 1),
    notes: '',
  }));
  const [erreurs, setErreurs] = useState<ErreursNouveauLead>({});
  // Cibles de `signalerErreursDeChamps`, dans l'ordre du formulaire (locales ou renvoyees par l'API).
  const refEmail = useRef<HTMLInputElement>(null);
  const refSiteWeb = useRef<HTMLInputElement>(null);
  const refContactNom = useRef<HTMLInputElement>(null);
  const refCommercial = useRef<HTMLSelectElement>(null);
  const refDate = useRef<HTMLInputElement>(null);
  const modifier = (suivants: Partial<ChampsNouveauLead>): void => setChamps((precedents) => ({ ...precedents, ...suivants }));

  const signaler = (suivantes: ErreursNouveauLead): void =>
    signalerErreursDeChamps(() => setErreurs(suivantes), [
      [suivantes.telephone, refPremierChamp],
      [suivantes.email, refEmail],
      [suivantes.siteWeb, refSiteWeb],
      [suivantes.contactNom, refContactNom],
      [suivantes.proprietaireId, refCommercial],
      [suivantes.prochaineEtape, refDate],
    ]);

  // Une erreur deja affichee sous son champ ne double pas le bandeau general.
  const masquerBandeau = Object.values(erreurs).some(Boolean);

  const soumettre = (evenement: FormEvent<HTMLFormElement>): void => {
    evenement.preventDefault();
    const trouvees = verifierNouveauLead(champs);
    if (Object.keys(trouvees).length > 0) { signaler(trouvees); return; }
    setErreurs({});
    creer.mutate(corpsNouveauLead(entreprise.siret, champs, { attribuer, moiId: utilisateur?.id }), {
      onSuccess: (lead) => { toast.succes(`Lead « ${lead.denomination} » créé.`); onTermine(); naviguer(`/leads/${lead.id}`); },
      onError: (cause) => {
        // 400 VALIDATION : l'API designe le champ fautif (details[].champ, chemin du corps).
        const parChamp: ErreursNouveauLead = {
          telephone: erreurDeChamp(cause, 'telephone'),
          email: erreurDeChamp(cause, 'email'),
          siteWeb: erreurDeChamp(cause, 'siteWeb'),
          contactNom: erreurDeChamp(cause, 'contact.nom'),
          proprietaireId: erreurDeChamp(cause, 'proprietaireId'),
          // Type et note ne peuvent etre refuses (liste fermee, longueur bornee a la saisie) : seule la date peut l'etre,
          // et son message (« Date ISO invalide ») serait du jargon.
          prochaineEtape: erreurDeChamp(cause, 'prochaineEtape.date') === undefined ? undefined : MESSAGE_DATE_REFUSEE,
        };
        if (Object.values(parChamp).some(Boolean)) signaler(parChamp);
      },
    });
  };

  const champTexte = (libelle: string, cle: 'telephone' | 'email' | 'siteWeb' | 'contactPrenom' | 'contactNom' | 'contactFonction',
    options: { type?: 'tel' | 'email'; inputMode?: 'url'; placeholder?: string; erreur?: string; ref?: RefObject<HTMLInputElement | null> } = {}) => (
    <Champ libelle={libelle} erreur={options.erreur}>
      {(id, decritPar) => (
        <input ref={options.ref} id={id} type={options.type ?? 'text'} inputMode={options.inputMode} className="champ" placeholder={options.placeholder}
          aria-describedby={decritPar} aria-invalid={options.erreur ? true : undefined} value={champs[cle]}
          onChange={(evenement) => {
            const valeur = evenement.target.value;
            // Etat complet d'abord, puis la cle calculee : le resultat reste un `ChampsNouveauLead`.
            setChamps((precedents) => ({ ...precedents, [cle]: valeur }));
          }} />
      )}
    </Champ>
  );

  return (
    <form onSubmit={soumettre} noValidate className="space-y-5">
      <IdentiteOfficielle entreprise={entreprise} />

      <fieldset className="space-y-3">
        <legend className="text-sm font-semibold text-encre">Coordonnées</legend>
        <div className="grid gap-3 sm:grid-cols-3">
          {champTexte('Téléphone', 'telephone', { type: 'tel', ref: refPremierChamp, erreur: erreurs.telephone })}
          {champTexte('E-mail', 'email', { type: 'email', ref: refEmail, erreur: erreurs.email })}
          {champTexte('Site web', 'siteWeb', { inputMode: 'url', placeholder: 'www.entreprise.fr', ref: refSiteWeb, erreur: erreurs.siteWeb })}
        </div>
      </fieldset>

      <fieldset className="space-y-3">
        <legend className="text-sm font-semibold text-encre">Contact principal</legend>
        <p className="aide">Facultatif : il rejoindra les contacts du lead.</p>
        <div className="grid gap-3 sm:grid-cols-3">
          {champTexte('Prénom', 'contactPrenom')}
          {champTexte('Nom', 'contactNom', { ref: refContactNom, erreur: erreurs.contactNom })}
          {champTexte('Fonction', 'contactFonction', { placeholder: 'Gérant, directrice…' })}
        </div>
      </fieldset>

      <fieldset className="space-y-3">
        <legend className="text-sm font-semibold text-encre">Suivi</legend>
        {attribuer && (
          <Champ libelle="Commercial" erreur={erreurs.proprietaireId}>
            {(id, decritPar) => (
              <ChampCommercial ref={refCommercial} id={id} valeur={champs.proprietaireId} invalide={Boolean(erreurs.proprietaireId)} decritPar={decritPar}
                onChange={(proprietaireId) => modifier({ proprietaireId })} />
            )}
          </Champ>
        )}
        <CaseACocher libelle="Planifier une première étape" coche={champs.planifier} onChange={(planifier) => modifier({ planifier })} />
        {champs.planifier && (
          <ChampsProchaineEtape valeur={champs.prochaineEtape} erreur={erreurs.prochaineEtape} refDate={refDate} onChange={(prochaineEtape) => modifier({ prochaineEtape })} />
        )}
      </fieldset>

      <Champ libelle="Notes">
        {(id) => (
          <textarea id={id} rows={3} className="champ-zone" placeholder="Ce que vous savez déjà de cette entreprise…"
            value={champs.notes} onChange={(evenement) => modifier({ notes: evenement.target.value })} />
        )}
      </Champ>

      {creer.isError && !masquerBandeau && <ErreurCreation erreur={creer.error} onOuvrir={onTermine} />}

      <div className="flex flex-wrap justify-end gap-2 border-t border-bordure pt-4">
        {/* Indisponible pendant la creation (sa suite serait perdue), mais atteignable : `disabled` ferait perdre le focus. */}
        <Bouton variante="discret" icone={<ArrowLeft aria-hidden="true" />} aria-disabled={creer.isPending || undefined}
          onClick={() => { if (!creer.isPending) onRetour(); }}>
          Retour à la recherche
        </Bouton>
        <Bouton type="submit" variante="primaire" chargement={creer.isPending}>Créer le lead</Bouton>
      </div>
    </form>
  );
}
